// ─────────────────────────────────────────────────────────────
// ARCHIVO: src/pages/PagoExitosoPage.js
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const PLAN_NAMES = {
  starter: 'Starter', grupo: 'Grupo', empresa: 'Empresa',
  grande: 'Grande', masivo: 'Masivo',
};

const PLAN_MAX_MEMBERS = {
  starter: 10, grupo: 20, empresa: 35, grande: 60, masivo: 100,
};

const FUNCTIONS_BASE = 'https://us-central1-pitazo-mundial.cloudfunctions.net';

export default function PagoExitosoPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [params] = useSearchParams();

  const planId          = params.get('plan');
  const groupId         = params.get('group');
  const provider        = params.get('provider');
  const orderId         = params.get('token');          // PayPal
  const paymentId       = params.get('payment_id');     // MercadoPago
  const mpStatus        = params.get('status');         // MercadoPago: 'approved'
  const merchantOrderId = params.get('merchant_order_id');

  const [dots, setDots]         = useState('');
  const [pageStatus, setPageStatus] = useState('loading');
  const [errorMsg, setErrorMsg]     = useState('');

  const marcarUsuarioPagado = async () => {
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { hasActivePaidGroup: true });
    } catch (err) { console.error('Error hasActivePaidGroup:', err); }
  };

  // ── CLAVE: activa el grupo directamente sin esperar webhook ──
  const activarGrupo = async () => {
    if (!groupId || !planId) return;
    const maxMembers = PLAN_MAX_MEMBERS[planId] || 10;
    await updateDoc(doc(db, 'groups', groupId), {
      status: 'active',
      plan: planId,
      maxMembers,
      activatedAt: serverTimestamp(),
      activatedBy: 'pago_exitoso_page',
    });
    // Registrar en payments para que aparezca en el SuperAdmin
    await addDoc(collection(db, 'payments'), {
      groupId,
      userId: currentUser?.uid || null,
      planId,
      planName: PLAN_NAMES[planId] || planId,
      provider: provider || 'mercadopago',
      paymentId: paymentId || orderId || null,
      merchantOrderId: merchantOrderId || null,
      amount: null,
      currency: provider === 'paypal' ? 'USD' : 'COP',
      status: 'approved',
      createdAt: serverTimestamp(),
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 500);

    // ── PayPal ──────────────────────────────────────────────
    const capturarPayPal = async () => {
      try {
        const res = await fetch(`${FUNCTIONS_BASE}/capturarPagoPayPal`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId }),
        });
        const data = await res.json();
        if (data.success) {
          await activarGrupo();
          await marcarUsuarioPagado();
          setPageStatus('success');
        } else {
          setPageStatus('error');
          setErrorMsg(data.error || 'Error al confirmar el pago con PayPal');
        }
      } catch (err) {
        setPageStatus('error');
        setErrorMsg('Error de conexión al confirmar el pago');
      }
    };

    // ── MercadoPago ─────────────────────────────────────────
    const procesarMercadoPago = async () => {
      try {
        // MP redirige con ?status=approved cuando aprobó
        // Si no hay status (MP antiguo) también asumimos éxito
        if (!mpStatus || mpStatus === 'approved') {
          await activarGrupo();
          await marcarUsuarioPagado();
          setPageStatus('success');
        } else {
          setPageStatus('error');
          setErrorMsg(
            mpStatus === 'pending'
              ? 'Tu pago está pendiente de aprobación. Cuando se confirme, tu grupo se activará automáticamente.'
              : `El pago no fue aprobado (estado: ${mpStatus}). Si ya se hizo el cobro, contáctanos.`
          );
        }
      } catch (err) {
        // Aunque falle el update, mostramos éxito porque el webhook de MP igual llega
        console.error('Error activando grupo:', err);
        await marcarUsuarioPagado();
        setPageStatus('success');
      }
    };

    if (provider === 'paypal' && orderId) {
      capturarPayPal();
    } else {
      procesarMercadoPago();
    }

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (pageStatus !== 'success') return;
    const timer = setTimeout(() => navigate('/admin'), 5000);
    return () => clearTimeout(timer);
  }, [pageStatus, navigate]);

  return (
    <div style={{
      minHeight: '100vh', background: '#080810',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '2rem', fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@700&family=Inter:wght@400;500&display=swap');
        @keyframes bounceIn { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.2);opacity:1} 100%{transform:scale(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      {/* LOADING */}
      {pageStatus === 'loading' && (
        <>
          <div style={{ width:60, height:60, borderRadius:'50%', border:'4px solid rgba(255,255,255,0.1)', borderTop:'4px solid #F0B429', animation:'spin 0.9s linear infinite', marginBottom:'1.5rem' }} />
          <div style={{ color:'#F5F5F0', fontSize:'1rem', textAlign:'center' }}>
            {provider === 'paypal' ? `Confirmando pago con PayPal${dots}` : `Activando tu plan${dots}`}
          </div>
        </>
      )}

      {/* ÉXITO */}
      {pageStatus === 'success' && (
        <>
          <div style={{ width:100, height:100, borderRadius:'50%', background:'rgba(6,184,106,0.15)', border:'3px solid #06B86A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem', animation:'bounceIn 0.6s cubic-bezier(0.36,0.07,0.19,0.97)', marginBottom:'1.5rem' }}>
            ✅
          </div>
          <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:'1.8rem', fontWeight:700, letterSpacing:3, color:'#F5F5F0', textAlign:'center', animation:'fadeUp 0.5s ease 0.3s both', marginBottom:'0.5rem' }}>
            ¡PAGO EXITOSO!
          </div>
          <div style={{ color:'#06B86A', fontSize:'1rem', animation:'fadeUp 0.5s ease 0.5s both', marginBottom:'2rem', textAlign:'center' }}>
            Plan <strong>{PLAN_NAMES[planId] || planId}</strong> activado 🎉
          </div>
          <div style={{ background:'#14141f', border:'1px solid rgba(255,255,255,0.07)', borderRadius:16, padding:'1.5rem', maxWidth:360, width:'100%', textAlign:'center', animation:'fadeUp 0.5s ease 0.7s both', marginBottom:'2rem' }}>
            <div style={{ fontSize:'0.85rem', color:'#6B6B7B', lineHeight:1.7 }}>
              Tu grupo ya está activo con los nuevos cupos disponibles.
              <br /><br />
              <span style={{ color:'#F0B429' }}>Redirigiendo a tu panel{dots}</span>
            </div>
          </div>
          <button onClick={() => navigate('/admin')} style={{ padding:'12px 32px', background:'linear-gradient(135deg,#F0B429,#C9820A)', border:'none', borderRadius:12, fontFamily:"'Oswald',sans-serif", fontSize:'0.9rem', fontWeight:700, letterSpacing:2, color:'#080810', cursor:'pointer', animation:'fadeUp 0.5s ease 0.9s both' }}>
            IR A MI PANEL →
          </button>
        </>
      )}

      {/* ERROR */}
      {pageStatus === 'error' && (
        <>
          <div style={{ width:100, height:100, borderRadius:'50%', background:'rgba(220,38,38,0.15)', border:'3px solid #DC2626', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'3rem', animation:'bounceIn 0.6s cubic-bezier(0.36,0.07,0.19,0.97)', marginBottom:'1.5rem' }}>
            ❌
          </div>
          <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:'1.8rem', fontWeight:700, letterSpacing:3, color:'#F5F5F0', textAlign:'center', marginBottom:'0.5rem' }}>
            ERROR AL CONFIRMAR
          </div>
          <div style={{ color:'#DC2626', fontSize:'0.9rem', marginBottom:'2rem', textAlign:'center', maxWidth:340 }}>
            {errorMsg}
            <br /><br />
            <span style={{ color:'#6B6B7B', fontSize:'0.8rem' }}>
              Si ya se realizó el cobro, contáctanos y lo resolvemos.
            </span>
          </div>
          <button onClick={() => navigate('/admin')} style={{ padding:'12px 32px', background:'linear-gradient(135deg,#F0B429,#C9820A)', border:'none', borderRadius:12, fontFamily:"'Oswald',sans-serif", fontSize:'0.9rem', fontWeight:700, letterSpacing:2, color:'#080810', cursor:'pointer' }}>
            IR A MI PANEL →
          </button>
        </>
      )}
    </div>
  );
}